import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { fetchAnalyticsView } from "../ExtendedAnalytics/api"
import AnalyticsFilterFields from "../ExtendedAnalytics/AnalyticsFilterFields"
import { QuerySummary } from "../ExtendedAnalytics/DashboardComponents"
import ExportCurrentView from "../ExtendedAnalytics/ExportCurrentView"
import { changeQuery, querySearch, readQuery } from "../ExtendedAnalytics/queryState"
import { FEEDBACK_VIEW, FeedbackCards, feedbackKpis } from "./FeedbackCards"
import { OrganizerTable, ReasonsTable } from "./FeedbackTables"
import PlayerTexts from "./PlayerTexts"

// CP „Tournament feedback” › Analysis (sportya-api docs/tournament-feedback.md, „Analytics: view-ul tournament-feedback”;
// docs/design/tournament-feedback, screenshots 06 and 07): the Extended Analytics filters (period, comparison, sport,
// activity location), one card per rated theme, the organizer and reasons tables, the player texts and the export of the
// snapshot on screen.

// No custom range on this tab (approved design): the other periods, from the address, Last 30 days by default.
const HIDDEN_PERIODS = ["custom"]
export function readFeedbackQuery(search) {
  const params = new URLSearchParams(search)
  if (!params.get("period") || HIDDEN_PERIODS.includes(params.get("period"))) {
    params.set("period", "last-30-days")
    params.delete("startDate")
    params.delete("endDate")
  }
  const { lens, view, ...query } = readQuery(params.toString())
  return { view: FEEDBACK_VIEW, ...query }
}

const ACCESS = "Active SuperAdmin access is required."

export default function FeedbackAnalysis() {
  const history = useHistory()
  const location = useLocation()
  const key = querySearch(readFeedbackQuery(location.search))
  const query = useMemo(() => readFeedbackQuery(key), [key])
  // `read` belongs to the selection `key`; a read of another selection is never shown under this one.
  const [read, setRead] = useState({ key: null })
  const [loading, setLoading] = useState(true)
  const [shown, setShown] = useState(null) // the last snapshot, for the filters' options while the next one loads
  const [attempt, setAttempt] = useState(0)
  const [denied, setDenied] = useState(false)
  const bypassCache = useRef(false)

  useEffect(() => {
    let active = true
    const refresh = bypassCache.current
    bypassCache.current = false
    setLoading(true)
    fetchAnalyticsView(refresh ? { ...query, refresh: "true" } : query).then(response => {
      if (!active) return
      setRead({ key, snapshot: response.data })
      setShown(response.data)
      setLoading(false)
    }).catch(error => {
      if (!active) return
      if ([401, 403].includes(error?.response?.status)) setDenied(true)
      setRead({ key, error: true })
      setLoading(false)
    })
    return () => { active = false }
  }, [key, query, attempt])

  const refresh = useCallback(() => {
    bypassCache.current = true
    setAttempt(value => value + 1)
  }, [])
  const deny = useCallback(() => setDenied(true), [])
  const update = changes => history.push({ pathname: location.pathname, search: querySearch(changeQuery(query, changes)) })

  const current = read.key === key ? read : { key }
  const snapshot = current.snapshot
  if (denied) return <section aria-label="Analysis"><p role="alert">{ACCESS}</p></section>
  return <section aria-label="Analysis" className="tournament-feedback-analysis analytics-theme">
    <section className="analytics-control-panel" aria-label="Feedback filters">
      <div className="analytics-filter-grid">
        <AnalyticsFilterFields query={query} snapshot={shown} onChange={update} hiddenPeriods={HIDDEN_PERIODS} />
      </div>
      {snapshot && <QuerySummary snapshot={snapshot} />}
      <p className="analytics-filter-note">The same filters as Extended Analytics. Active feedback only.</p>
    </section>
    <div className="analytics-metadata tournament-feedback-analysis__toolbar">
      <ExportCurrentView snapshot={snapshot} query={query} onAccessRequired={deny} />
      <button type="button" onClick={refresh} disabled={loading}>{loading && snapshot ? "Refreshing…" : "Refresh"}</button>
    </div>
    {!snapshot && !current.error && <div className="analytics-state analytics-loading-state" role="status">
      <span className="analytics-loader" aria-hidden="true" /><span>Loading analytics…</span>
    </div>}
    {current.error && <div className="analytics-state is-error" role="alert">
      <strong>We could not load this view.</strong>
      <button type="button" onClick={refresh} disabled={loading}>Try again</button>
    </div>}
    {snapshot?.metadata?.empty && <div className="card"><div className="card-body text-center text-muted" role="status">
      No tournament feedback in this selection.
    </div></div>}
    {snapshot && !snapshot.metadata?.empty && <>
      <FeedbackCards kpis={feedbackKpis(snapshot)} />
      <OrganizerTable feedback={snapshot.tournamentFeedback} />
      <ReasonsTable feedback={snapshot.tournamentFeedback} />
      <PlayerTexts query={query} snapshot={snapshot} onRefresh={refresh} onAccessRequired={deny} />
    </>}
  </section>
}
